import { useEffect } from 'react';
import {
  Button,
  Form,
  Input,
  Space,
  Typography,
  Alert
} from 'antd';
import {
  CloseCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { withDevModalFilePath } from '../common';
import type { WorkScheduleDetail } from './DetailModal';

const { Modal } = withDevModalFilePath('client/src/components/WorkSchedule/RejectReasonModal.tsx');
const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface RejectReasonModalProps {
  open: boolean;
  schedule: WorkScheduleDetail | null;
  onSubmit: (reason: string) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

/**
 * 差し戻し理由入力モーダル
 * - 差し戻し理由を入力（必須）
 * - 作業者へのフィードバックを記載
 */
export function RejectReasonModal({
  open,
  schedule,
  onSubmit,
  onClose,
  loading = false
}: RejectReasonModalProps): JSX.Element {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values.reason);
      form.resetFields();
    } catch {
      // バリデーションエラーは何もしない
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <CloseCircleOutlined />
          <span>作業を差し戻す</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          キャンセル
        </Button>,
        <Button
          key="submit"
          type="primary"
          danger
          icon={<CloseCircleOutlined />}
          loading={loading}
          onClick={handleSubmit}
        >
          差し戻す
        </Button>
      ]}
      width={500}
      destroyOnHidden
    >
      {schedule && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message="差し戻し確認"
            description={
              <div>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text strong>{schedule.title || '名称未設定のスケジュール'}</Text>
                </Paragraph>
                <Text type="secondary">
                  この作業を差し戻します。差し戻し理由を入力してください。
                  作業者は再度作業を実施し、完了報告を送信する必要があります。
                </Text>
              </div>
            }
            type="warning"
            icon={<WarningOutlined />}
            showIcon
          />

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              reason: ''
            }}
          >
            <Form.Item
              label="差し戻し理由"
              name="reason"
              rules={[
                { required: true, message: '差し戻し理由を入力してください' },
                { min: 10, message: '差し戻し理由は10文字以上入力してください' }
              ]}
            >
              <TextArea
                rows={5}
                placeholder="差し戻しの理由を具体的に入力してください（例：写真が不鮮明なため、再度撮影してください）"
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Modal>
  );
}
